// §27 shareable TCO cards. The card is rendered server-side to a PNG
// (tdewolff/canvas, see features/tco/sharecard_render.go); this module only
// fetches that PNG and hands it to the OS share sheet (navigator.share), with a
// download fallback where file-sharing is unavailable (desktop Safari, older
// browsers). Neither the share sheet nor the fetch has a server-side equivalent,
// so this stays client-side – but it no longer rasterizes anything (the earlier
// modern-screenshot dependency and the document.fonts.ready dance are gone).

// shareCardText reads the localized error strings the bundle can't get from Go
// i18n directly, off a hidden #sharecard-i18n span (same bridge as #intake-i18n).
function shareCardText(): { errCapture: string; errShare: string } {
  const el = document.getElementById('sharecard-i18n');
  return {
    errCapture: el?.getAttribute('data-err-capture') || 'Could not load the image.',
    errShare: el?.getAttribute('data-err-share') || 'Sharing failed.',
  };
}

function notify(msg: string): void {
  if (typeof window.__notify === 'function') void window.__notify(msg, 'error');
}

async function shareCard(trigger: HTMLButtonElement): Promise<void> {
  // Share exactly the image currently shown in this slide (honours the
  // amounts toggle, which rewrites the <img> src).
  const img = trigger.closest('.story-slide')?.querySelector<HTMLImageElement>('.story-card-img');
  const url = img?.src;
  const filename = trigger.dataset.filename || 'libretto-card.png';
  const title = trigger.dataset.title || 'Libretto';
  if (!url) return;
  const t = shareCardText();
  trigger.disabled = true;
  trigger.classList.add('is-loading');
  try {
    let blob: Blob;
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error('status ' + res.status);
      blob = await res.blob();
    } catch {
      notify(t.errCapture);
      return;
    }

    const file = new File([blob], filename, { type: 'image/png' });
    const shareData: ShareData = { files: [file], title };
    if (navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        // AbortError = the user dismissed the share sheet; not a failure.
        if ((e as DOMException)?.name !== 'AbortError') notify(t.errShare);
      }
      return;
    }

    // Fallback: download the PNG.
    const objURL = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objURL;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objURL);
  } finally {
    trigger.disabled = false;
    trigger.classList.remove('is-loading');
  }
}

// __shareCard type lives in globals.d.ts.
window.__shareCard = (trigger) => {
  void shareCard(trigger);
};

export {};
